import { fromJS } from 'immutable';
import {
  FIELD_NAMES,
  GDPR_SETTING,
  LEGACY_CONSENT_FIELD_NAMES,
  EXPLICIT_CONSENT_FIELD_NAMES,
  LEGACY_GDPR_SETTINGS,
  OPTIN_RADIO_DEFAULTS,
  OPTIN_FORMATS,
  DISCLAIMER_LOCATION_TYPES,
} from 'utils/constants';
import { errorTextImpliedHasInvalidField, ERROR_TEXT_CHECKED_FIELD_NO_TEXT } from 'langs/en/errors';
import { makeImmutable } from 'utils/immutableUtils';
import { standardFieldNameToTaxiFieldName } from 'utils/coercionUtils';
import ConditionalityGraph from 'utils/conditionality.graph';
import { generateInvalidFieldData } from 'utils/editorDefaultStates';
import { createUIState } from './ops.ui';
import { updateFieldOverrideState, updateFormValidity } from './ops.field';
import { addLegacyConditionToPhone, removeLegacyConditionFromPhone } from './ops.conditionality';

export function setForm(state, form, shouldRebuildCollapsedState) {
  let fieldOrderCollapsedState = state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']) && state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']).toJS();
  const visibleBranches = state.getIn(['meta', 'uiState', 'visibleBranches']);
  if (shouldRebuildCollapsedState) {
    fieldOrderCollapsedState = Array(form.fieldOrder.length).fill(false);
  }
  const graph = new ConditionalityGraph({});
  const fields = Object.values(form.fields)
    .map((field) => ({ id: field.id, condition: field.condition, name: field.name }));
  const stateToFieldIdMap = fields.reduce((memo, field) => {
    memo[`state.${standardFieldNameToTaxiFieldName(field.name)}`] = field.id; // eslint-disable-line no-param-reassign
    return memo;
  }, {});
  fields.forEach((field) => {
    if (field.condition) {
      const conditionalityArr = field.condition.split(' ');
      const conditionalAction = conditionalityArr.slice(0, 2).join(' ');
      const fieldVertex = graph.findOrCreateVertex({ left: field.id, action: conditionalAction });
      graph.importConditionString(fieldVertex, conditionalityArr.slice(2).join(' '), stateToFieldIdMap);
      form.fields[field.id].condition = ''; // eslint-disable-line no-param-reassign
    } else {
      const fieldVertex = graph.findOrCreateVertex({ left: field.id, action: 'render if' });
      graph.addPath([0, fieldVertex], fieldVertex);
    }
  });
  if (form.fieldConditionalityInfo && form.fieldConditionalityInfo.branchingFields) {
    Object.keys(form.fieldConditionalityInfo.branchingFields).forEach((fieldId) => {
      graph.setUserBranches(form.fields[fieldId].id, form.fieldConditionalityInfo.branchingFields[fieldId]);
    });
  }

  const formUIState = makeImmutable(createUIState(form, fieldOrderCollapsedState, visibleBranches));
  return state
    .set('form', makeImmutable(form))
    .setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], makeImmutable(graph.dehydrate()))
    .setIn(['meta', 'uiState'], formUIState)
    .setIn(['meta', 'invalidFormData'], makeImmutable(generateInvalidFieldData(form.fields)));
}

function validateDisclaimerOverride(state, consentStyle) {
  const overrideKeyPathForType = (disclaimerType) => ['syntheticContentEntities', 'disclaimers', disclaimerType, 'override'];
  const clearOverrideValidation = (stateToClear, disclaimerType) => updateFormValidity(stateToClear, overrideKeyPathForType(disclaimerType), true).mergeIn(['meta', 'uiState', ...overrideKeyPathForType(disclaimerType)], {
    isValid: true,
    errorDisplay: '',
    className: '',
  });

  if (consentStyle !== GDPR_SETTING.GEO_IMPLIED) {
    return Object.values(DISCLAIMER_LOCATION_TYPES)
      .reduce((newState, disclaimerType) => clearOverrideValidation(newState, disclaimerType), state);
  }

  return Object.values(DISCLAIMER_LOCATION_TYPES)
    .reduce((newState, disclaimerType) => {
      const disclaimer = newState.getIn(['form', 'syntheticContentEntities', 'disclaimers', disclaimerType]);
      const show = disclaimer?.get('show');
      if (!show) {
        return clearOverrideValidation(newState, disclaimerType);
      }

      const value = disclaimer?.get('override');
      const isInvalid = !value || value.trim().length === 0;

      if (!isInvalid) {
        return newState;
      }

      return updateFormValidity(newState, overrideKeyPathForType(disclaimerType), false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT, true);
    }, state);
}

export function changeConsentStyle(state, consentStyle) {
  const consentStyleKeypath = ['form', 'syntheticContentEntities', 'gdprSetting'];
  const oldConsentStyle = state.getIn(consentStyleKeypath);
  const consentRestricted = [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.ALWAYS].includes(consentStyle);
  const usesImpliedConsent = [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.GEO_IMPLIED].includes(consentStyle);

  let newState = state;
  const fields = Object.values(newState.getIn(['form', 'fields']).toJS());
  fields.forEach((field) => {
    if (field.delete) return;

    if (field.name === FIELD_NAMES.LEAD_SHARE_OPT_IN) {
      const [newFormat, defaultKeypath, newDefault] = consentRestricted ? [OPTIN_FORMATS.RADIO, 'defaultRadio', OPTIN_RADIO_DEFAULTS.NONE] : [OPTIN_FORMATS.CHECKBOX, 'defaultChecked', false]; // defaultChecked set to false to keep behavior consistent between new form creation and consent style changes.
      newState = newState.setIn(['form', 'fields', field.id, 'editors', 'optInData', 'format'], newFormat)
        .setIn(['form', 'fields', field.id, 'editors', 'optInData', defaultKeypath], newDefault);
    }
    const credentialType = state.getIn(['meta', 'availableCredentialTypes', 0, 'value']);
    const keypath = ['fields', field.id, 'name'];
    let errorMessage;
    let valid = true;
    if ((LEGACY_CONSENT_FIELD_NAMES.includes(field.name) && usesImpliedConsent) || (EXPLICIT_CONSENT_FIELD_NAMES.includes(field.name) && consentStyle === GDPR_SETTING.ALWAYS_IMPLIED)) {
      valid = false;
      errorMessage = errorTextImpliedHasInvalidField(field.name, credentialType);
    }
    newState = updateFormValidity(newState, keypath, valid, errorMessage);

    if (field.name === FIELD_NAMES.PHONE) {
      if (consentStyle === GDPR_SETTING.ALWAYS_IMPLIED) {
        newState = newState.setIn(['form', 'fields', field.id, 'required'], false);
        newState = newState.setIn(['form', 'fields', field.id, 'hidden'], false);
      }

      const oldConsentIsLegacy = LEGACY_GDPR_SETTINGS.includes(oldConsentStyle);
      const newConsentIsLegacy = LEGACY_GDPR_SETTINGS.includes(consentStyle);
      if (oldConsentIsLegacy !== newConsentIsLegacy) {
        const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
        if (newConsentIsLegacy) {
          addLegacyConditionToPhone(field.id, graph);
        } else {
          removeLegacyConditionFromPhone(field.id, graph);
        }
        newState = newState.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate()));
      }
    }
    newState = updateFieldOverrideState(newState, field.id, field.type, consentStyle);
  });

  newState = newState.setIn(consentStyleKeypath, consentStyle);
  const impliedConsent = !(LEGACY_GDPR_SETTINGS.includes(consentStyle));
  newState = newState.setIn(['form', 'formMetadata', 'formAttributesConfig', 'impliedConsent'], impliedConsent);
  newState = validateDisclaimerOverride(newState, consentStyle);
  return newState;
}
